import type { MutationOptions } from "@tanstack/react-query";

/**
 * react-query's channel for optimistic updates works like this: `onMutate` returns a snapshot of
 * whatever the optimistic write is about to overwrite, react-query stores that snapshot, and hands
 * it back to `onError` / `onSuccess` / `onSettled` as their trailing `context` argument. That is
 * how a failed mutation knows what to roll back to.
 *
 * `makeMutation` has two independent layers of callbacks — one declared on the factory, one passed
 * at the hook call — and react-query has room for exactly one snapshot. These mergers reconcile
 * that: they keep one snapshot per layer inside a single wrapper object, then unwrap it so each
 * layer's callbacks only ever see the snapshot their own layer produced.
 */

const LAYER_CONTEXTS = Symbol("mergedMutationLayerContexts");

/** The single value handed to react-query in place of the two layers' separate snapshots. */
export interface MergedMutationContext<TConfigContext = unknown, TOptionContext = unknown> {
  [LAYER_CONTEXTS]: {
    config: TConfigContext | undefined;
    option: TOptionContext | undefined;
  };
}

type MutationCallbacks<TData, TError, TVariables, TContext> = Pick<
  MutationOptions<TData, TError, TVariables, TContext>,
  "onMutate" | "onSuccess" | "onError" | "onSettled"
>;

type OnMutate<TVariables, TContext> = MutationOptions<unknown, unknown, TVariables, TContext>["onMutate"];

/** What react-query accepts back from `onMutate`: a snapshot, a promise of one, or nothing. */
type OnMutateResult<TContext> = Promise<TContext | undefined> | TContext | undefined;

const isThenable = (value: unknown): value is PromiseLike<unknown> =>
  typeof value === "object" && value !== null && "then" in value && typeof value.then === "function";

/**
 * A fresh object here is never `===` the mutation's previous context, which makes react-query
 * dispatch a second `loading` action and notify observers again. Most mutations have nothing to
 * snapshot, so collapsing "no snapshots" back to `undefined` keeps their render count unchanged.
 */
const packLayerContexts = <TConfigContext, TOptionContext>(
  config: TConfigContext | undefined,
  option: TOptionContext | undefined,
): MergedMutationContext<TConfigContext, TOptionContext> | undefined =>
  config === undefined && option === undefined ? undefined : { [LAYER_CONTEXTS]: { config, option } };

/**
 * Waits for the config layer's snapshot before asking the option layer for its own, which is what
 * keeps an async config layer in the same "config first, then option" order a synchronous one has.
 *
 * Overlapping the two layers would be marginally faster, but they are not independent: both write
 * to the same query keys, and the standard optimistic update awaits `cancelQueries` before it
 * writes. An option layer that wrote while the config layer's `cancelQueries` was still in flight
 * could have that write clobbered by the very refetch the call was meant to cancel. Whether it did
 * would depend on where each layer happened to `await`, making one layer's ordering a property of
 * the other layer's internals.
 */
const packSettledLayers = async <TConfigContext, TOptionContext>(
  configContext: OnMutateResult<TConfigContext>,
  runOptionLayer: () => OnMutateResult<TOptionContext>,
) => packLayerContexts(await configContext, await runOptionLayer());

const mergeOnMutate =
  <TVariables, TConfigContext, TOptionContext>(
    configFn: OnMutate<TVariables, TConfigContext>,
    optionFn: OnMutate<TVariables, TOptionContext>,
  ) =>
  (variables: TVariables) => {
    const configContext = configFn?.(variables);
    if (isThenable(configContext)) return packSettledLayers(configContext, () => optionFn?.(variables));

    const optionContext = optionFn?.(variables);
    if (isThenable(optionContext)) return packSettledLayers(configContext, () => optionContext);

    // Neither layer went async, so hand the snapshots straight back. react-query awaits whatever
    // `onMutate` returns before it fires the request, and a promise costs a microtask even when it
    // is already resolved — worth avoiding across every mutation in the repo.
    return packLayerContexts(configContext, optionContext);
  };

/**
 * Combines a `makeMutation` factory's callbacks with the ones its hook caller passed. The factory's
 * runs first at every stage — callers rely on the mutation's own bookkeeping being done before
 * their handler sees the result. In `onMutate` that holds even when a layer is async: the
 * factory's callback settles before the hook caller's is invoked at all.
 *
 * Neither layer gets a snapshot when the *other* one's `onMutate` throws or rejects. react-query
 * only stores a context that `onMutate` returned, and a failed `onMutate` returns nothing, so the
 * mutation goes straight to `onError` with `context` undefined even for a layer that already
 * completed. A layer that makes an optimistic write is therefore the only place that can be
 * trusted to roll it back — don't rely on the other layer's `onError` to clean up after you.
 *
 * A callback the merged mutation has no use for collapses to `undefined` rather than to an empty
 * wrapper, so react-query's own `?.` short-circuits instead of awaiting a function that does
 * nothing.
 *
 * Return values from `onSuccess` / `onError` / `onSettled` are deliberately dropped rather than
 * awaited. react-query awaits those before it dispatches the terminal state, so propagating them
 * would make an async callback delay every consumer's `isSuccess` — a change in timing that has
 * nothing to do with the rollback channel this module exists to restore.
 *
 * Only these two layers get an unwrapped snapshot. `mutate(variables, { onError })`'s own
 * callbacks are invoked by react-query's observer, which hands them the wrapper as-is — there is
 * no third layer to key on, so they see an opaque object. Put anything that needs a snapshot in
 * the hook options instead, where it also survives the component unmounting mid-flight.
 */
export const mergeMutationCallbacks = <TData, TError, TVariables, TConfigContext, TOptionContext>(
  config: MutationCallbacks<TData, TError, TVariables, TConfigContext>,
  option: MutationCallbacks<TData, TError, TVariables, TOptionContext>,
): MutationCallbacks<TData, TError, TVariables, MergedMutationContext<TConfigContext, TOptionContext>> => ({
  onMutate: config.onMutate || option.onMutate ? mergeOnMutate(config.onMutate, option.onMutate) : undefined,

  onSuccess:
    config.onSuccess || option.onSuccess
      ? (data, variables, context) => {
          const layers = context?.[LAYER_CONTEXTS];
          config.onSuccess?.(data, variables, layers?.config);
          option.onSuccess?.(data, variables, layers?.option);
        }
      : undefined,

  onError:
    config.onError || option.onError
      ? (error, variables, context) => {
          const layers = context?.[LAYER_CONTEXTS];
          config.onError?.(error, variables, layers?.config);
          option.onError?.(error, variables, layers?.option);
        }
      : undefined,

  onSettled:
    config.onSettled || option.onSettled
      ? (data, error, variables, context) => {
          const layers = context?.[LAYER_CONTEXTS];
          config.onSettled?.(data, error, variables, layers?.config);
          option.onSettled?.(data, error, variables, layers?.option);
        }
      : undefined,
});
